import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, LOGO_URL, fmtDate } from "@/lib/api";
import { generatePdfBase64, makeDocFilename } from "@/lib/pdf";
import SignedDeliveryNote from "@/components/SignedDeliveryNote";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckSquare, Square, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// ── Inline SignaturePad (self-contained, no Layout dependency) ─────────────────
function SignaturePad({ onCapture }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    const pos = getPos(e);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#032352";
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1.5, 0, Math.PI * 2);
    ctx.fill();
    setEmpty(false);
  };

  const move = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.strokeStyle = "#032352";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const stop = () => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    onCapture(canvasRef.current.toDataURL("image/png"));
  };

  const clear = (e) => {
    e.stopPropagation();
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setEmpty(true);
    onCapture(null);
  };

  return (
    <div className="relative border-2 border-[#032352] rounded-lg bg-white" style={{ height: 160 }}>
      <canvas
        ref={canvasRef}
        data-testid="public-signature-canvas"
        className="w-full h-full rounded-lg"
        style={{ display: "block", touchAction: "none", cursor: "crosshair" }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={stop}
      />
      {empty && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-slate-400 text-sm select-none">Draw your signature here</span>
        </div>
      )}
      {!empty && (
        <button
          type="button"
          onClick={clear}
          data-testid="public-clear-signature-btn"
          className="absolute top-2 right-2 text-xs bg-white border border-slate-200 rounded px-2 py-1 text-slate-500 hover:text-red-500 transition-colors z-10"
        >
          Clear
        </button>
      )}
    </div>
  );
}

// ── Main Public Sign Page ──────────────────────────────────────────────────────
export default function PublicSign() {
  const location = useLocation();
  const token = location.pathname.split("/sign/")[1] || "";
  const [state, setState] = useState("loading"); // loading | error | already_signed | form | prompt | success
  const [deliveryData, setDeliveryData] = useState(null);
  const [items, setItems] = useState([]);
  const [signedBy, setSignedBy] = useState("");
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Post-sign invoice prompt
  const [promptChoice, setPromptChoice] = useState(""); // "" | "other"
  const [altEmail, setAltEmail] = useState("");
  const [sendingInvoice, setSendingInvoice] = useState(false);

  // Hidden off-screen element for PDF generation after signing
  const [pendingSignData, setPendingSignData] = useState(null);
  const signedRef = useRef(null);

  const today = new Date();
  const todayISO = today.toISOString().slice(0, 10);
  const todayDisplay = `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;

  useEffect(() => {
    if (!token) { setState("error"); return; }
    api.get(`/public/sign/${token}`)
      .then(({ data }) => {
        if (data.already_signed) {
          setDeliveryData(data);
          setState("already_signed");
        } else {
          setDeliveryData(data);
          setItems((data.line_items || []).map((it) => ({ ...it, confirmed: true })));
          setState("form");
        }
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setState("error");
        } else {
          setState("error");
        }
      });
  }, [token]);

  const toggleItem = (idx) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, confirmed: !it.confirmed } : it)));

  const canSubmit = signedBy.trim().length > 0 && signature !== null && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // 1. Prepare signData for PDF generation
      const sigData = {
        signed_by: signedBy.trim(),
        signed_at: todayISO,
        signature_base64: signature,
        items_confirmed: items,
      };
      setPendingSignData(sigData);
      await new Promise((r) => setTimeout(r, 350)); // wait for hidden element render

      // 2. Generate signed PDF
      let pdf_base64 = null;
      const filename = makeDocFilename(
        deliveryData?.customer_tag,
        "SignedDeliveryNote",
        deliveryData?.invoice_number
      );
      try {
        if (signedRef.current) {
          pdf_base64 = await generatePdfBase64(signedRef.current, { singlePage: true });
        }
      } catch {
        // PDF generation failed — continue without attachment
      }

      // 3. Submit to backend
      await api.post(`/public/sign/${token}`, {
        ...sigData,
        pdf_base64,
        filename,
      });

      setPendingSignData(null);
      setState("prompt"); // show invoice copy prompt
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setState("already_signed");
      } else {
        toast.error(msg || "Failed to submit signature. Please try again.");
        setSubmitting(false);
      }
    }
  };

  const customerEmail = deliveryData?.customer_email || deliveryData?.email || "";
  const invoiceNumber = deliveryData?.invoice_number || "";
  const invoiceId = deliveryData?.id || "";

  const sendInvoiceCopy = async (email) => {
    if (!invoiceId || !email) { setState("success"); return; }
    setSendingInvoice(true);
    try {
      await api.post(`/invoices/${invoiceId}/send-auto-invoice`, { to_email: email });
      toast.success("Invoice sent!");
    } catch { toast.error("Failed to send invoice copy."); }
    finally { setSendingInvoice(false); setState("success"); }
  };

  const co = deliveryData?.company_details || {};
  const companyName = co.name || "J&L Industrial Supplies";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="public-sign-page">
      {/* Top bar */}
      <div className="bg-[#032352] text-white px-4 py-3 flex items-center gap-3 shadow-md">
        <div className="bg-white rounded-md p-1.5 shadow-sm shrink-0">
          <img src={LOGO_URL} alt="J&L Industrial Supplies" className="h-7 object-contain" crossOrigin="anonymous" />
        </div>
        <div className="h-6 w-px bg-white/20" />
        <span className="font-semibold text-sm tracking-wide">{companyName}</span>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* ── Loading ── */}
        {state === "loading" && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-[#032352]" />
            <p className="text-[#5B5C5F]">Loading delivery note…</p>
          </div>
        )}

        {/* ── Error ── */}
        {state === "error" && (
          <div className="bg-white border border-red-200 rounded-xl p-8 text-center mt-8">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">?</span>
            </div>
            <h2 className="font-heading text-xl font-bold text-[#032352] mb-2">Link Not Found</h2>
            <p className="text-[#5B5C5F] text-sm">
              This sign link is invalid or has been removed. Please contact {companyName} for a new link.
            </p>
          </div>
        )}

        {/* ── Already Signed ── */}
        {state === "already_signed" && (
          <div className="bg-white border border-emerald-200 rounded-xl p-8 text-center mt-8" data-testid="already-signed-state">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <h2 className="font-heading text-xl font-bold text-[#032352] mb-2">Already Signed</h2>
            <p className="text-[#5B5C5F] text-sm mb-4">
              Delivery note <strong>{deliveryData?.invoice_number}</strong> has already been signed
              {deliveryData?.signed_by ? ` by ${deliveryData.signed_by}` : ""}.
            </p>
            <p className="text-xs text-[#5B5C5F]">
              Please contact {companyName} if you have any questions.
            </p>
          </div>
        )}

        {/* ── Invoice Copy Prompt ── */}
        {state === "prompt" && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 mt-8 shadow-sm" data-testid="invoice-prompt-state">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <h2 className="font-heading text-xl font-bold text-[#032352] mb-2 text-center">Delivery Confirmed!</h2>
            <p className="text-[#5B5C5F] text-sm text-center mb-6">
              Would you like a copy of Invoice <strong>{invoiceNumber}</strong> sent to your email?
            </p>

            {promptChoice === "" && (
              <div className="flex flex-col gap-3">
                {customerEmail && (
                  <button
                    data-testid="prompt-yes-btn"
                    disabled={sendingInvoice}
                    onClick={() => sendInvoiceCopy(customerEmail)}
                    className="w-full py-3 rounded-lg bg-[#032352] text-white font-semibold text-sm hover:bg-[#04347A] transition-colors disabled:opacity-50"
                  >
                    {sendingInvoice ? "Sending…" : `Yes — send to ${customerEmail}`}
                  </button>
                )}
                <button
                  data-testid="prompt-other-btn"
                  onClick={() => setPromptChoice("other")}
                  className="w-full py-3 rounded-lg border border-[#032352] text-[#032352] font-semibold text-sm hover:bg-[#032352]/5 transition-colors"
                >
                  Send to a different email
                </button>
                <button
                  data-testid="prompt-no-btn"
                  onClick={() => setState("success")}
                  className="w-full py-3 rounded-lg border border-slate-200 text-[#5B5C5F] font-medium text-sm hover:bg-slate-50 transition-colors"
                >
                  No thanks
                </button>
              </div>
            )}

            {promptChoice === "other" && (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-[#5B5C5F] text-center">Enter the email address:</p>
                <input
                  data-testid="prompt-alt-email"
                  type="email"
                  value={altEmail}
                  onChange={(e) => setAltEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#032352]/20"
                  autoFocus
                />
                <button
                  data-testid="prompt-send-other-btn"
                  disabled={!altEmail.trim() || sendingInvoice}
                  onClick={() => sendInvoiceCopy(altEmail.trim())}
                  className="w-full py-3 rounded-lg bg-[#032352] text-white font-semibold text-sm hover:bg-[#04347A] transition-colors disabled:opacity-50"
                >
                  {sendingInvoice ? "Sending…" : "Send Invoice"}
                </button>
                <button onClick={() => setPromptChoice("")} className="text-xs text-[#5B5C5F] underline text-center">
                  Back
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Success ── */}
        {state === "success" && (
          <div className="bg-white border border-emerald-200 rounded-xl p-8 text-center mt-8" data-testid="sign-success-state">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <h2 className="font-heading text-xl font-bold text-[#032352] mb-2">Delivery Confirmed!</h2>
            <p className="text-[#5B5C5F] text-sm mb-2">
              Thank you, <strong>{signedBy}</strong>. Your signature for delivery note{" "}
              <strong>{deliveryData?.invoice_number}</strong> has been recorded.
            </p>
            {deliveryData?.customer_email && (
              <p className="text-xs text-[#5B5C5F]">
                A copy of the signed delivery note has been sent to <strong>{deliveryData.customer_email}</strong>.
              </p>
            )}
          </div>
        )}

        {/* ── Sign Form ── */}
        {state === "form" && deliveryData && (
          <div className="space-y-6">
            {/* Header card */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1">Delivery Note</p>
                  <p className="font-heading font-bold text-[#032352] text-xl">{deliveryData.invoice_number}</p>
                  <p className="text-[#5B5C5F] text-sm mt-0.5">{fmtDate(deliveryData.invoice_date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1">Deliver To</p>
                  <p className="font-semibold text-[#032352] text-sm">{deliveryData.customer_name}</p>
                  {deliveryData.order_number && (
                    <p className="text-xs text-[#5B5C5F] mt-0.5">Order: {deliveryData.order_number}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Items checklist */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-[#032352]">
                <p className="text-xs font-semibold text-white uppercase tracking-wider">Items Being Delivered</p>
              </div>
              <div className="p-4">
                <p className="text-xs text-slate-400 mb-3">Tap to untick any items NOT included in this delivery</p>
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                  {items.map((it, i) => (
                    <button
                      key={i}
                      data-testid={`public-sign-item-${i}`}
                      onClick={() => toggleItem(i)}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
                        it.confirmed ? "bg-white hover:bg-slate-50" : "bg-red-50 hover:bg-red-100"
                      }`}
                    >
                      {it.confirmed ? (
                        <CheckSquare className="w-5 h-5 text-emerald-500 shrink-0" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-300 shrink-0" />
                      )}
                      <span className="flex-1 text-[#032352] text-sm font-medium">
                        {it.title}
                        {it.variant ? <span className="text-[#5B5C5F] font-normal"> — {it.variant}</span> : null}
                      </span>
                      <span className="font-mono text-[#032352] text-sm font-semibold shrink-0">
                        ×{it.quantity}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Signature section */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5B5C5F] font-semibold mb-1">Date of Delivery</p>
                <p className="text-[#032352] font-semibold text-lg" data-testid="public-sign-date">{todayDisplay}</p>
              </div>

              <div>
                <Label htmlFor="public-signed-by" className="text-xs uppercase tracking-wider text-[#5B5C5F] font-semibold">
                  Received By (Print Name)
                </Label>
                <Input
                  id="public-signed-by"
                  data-testid="public-signed-by-input"
                  value={signedBy}
                  onChange={(e) => setSignedBy(e.target.value)}
                  placeholder="Full name of person accepting delivery"
                  className="mt-1 text-base h-12"
                  autoComplete="off"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B5C5F] font-semibold mb-2 block">Signature</Label>
                <SignaturePad onCapture={setSignature} />
              </div>

              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                data-testid="public-sign-submit-btn"
                className="w-full bg-[#032352] hover:bg-[#04347A] disabled:opacity-40 h-12 text-base"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-2" /> Confirm & Submit Signature</>
                )}
              </Button>
            </div>

            <p className="text-center text-xs text-[#5B5C5F] pb-6">
              By submitting, you confirm receipt of the items listed above on behalf of {deliveryData.customer_name}.
            </p>
          </div>
        )}
      </div>

      {/* Hidden off-screen SignedDeliveryNote for PDF generation */}
      <div style={{ position: "fixed", left: "-9999px", top: 0, width: "794px", opacity: 0, pointerEvents: "none", zIndex: -1 }}>
        <div ref={signedRef}>
          {pendingSignData && deliveryData && (
            <SignedDeliveryNote
              invoice={deliveryData}
              signatureData={{
                signed_by: pendingSignData.signed_by,
                signed_at: pendingSignData.signed_at,
                signature_base64: pendingSignData.signature_base64,
                items_confirmed: pendingSignData.items_confirmed,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
